<template>
  <article
    class="package-status-card"
    :style="getBaseTypeThemeStyle(row.key)"
    :class="{
      [getBaseTypeThemeClass(row.key)]: true,
      'package-status-card--empty': !active,
      'package-status-card--canceled': active?.ends_at,
    }"
  >
    <template v-if="active">
      <header class="package-status-card__header">
        <div>
          <div class="package-status-card__eyebrow">
            <Icon icon="fa-pro-solid:circle-check" />
            Aktives Paket
          </div>
          <h2 class="package-status-card__title">{{ row.label }}</h2>
        </div>
        <span
          class="package-status-card__badge"
          :class="{'package-status-card__badge--canceled': active.ends_at}"
        >
          {{ active.ends_at ? 'gekündigt' : 'läuft' }}
        </span>
      </header>

      <div class="package-status-card__spotlight">
        <div>
          <span class="package-status-card__spotlight-label">Ihr aktueller Umfang</span>
          <strong>{{ getTileQuantity(active) }}</strong>
        </div>
        <div class="package-status-card__price-block">
          <template v-if="isIndividualPackage(active)">
            <span>{{ getActivePackagePriceLabel(active) }}</span>
          </template>
          <PriceAmount
            v-else
            :cents="getPackageTotalPriceCents(active)"
            monthly
            block
          />
        </div>
      </div>

      <dl v-if="!isIndividualPackage(active)" class="package-status-card__facts mb-0">
        <div class="package-status-card__fact--full">
          <dt>Nächster Abrechnungszeitraum</dt>
          <dd>{{ getNextBillingPeriodStartLabel(active) }}</dd>
        </div>
      </dl>

      <div class="package-status-card__actions">
        <template v-if="isIndividualPackage(active)">
          <span class="package-tile__cta-wrap">
            <router-link :to="{name: 'account.support'}" class="btn btn-primary package-status-card__action">
              Paketanpassung anfragen
            </router-link>
            <button
              type="button"
              class="package-tile__info-btn"
              data-bs-toggle="popover"
              data-bs-trigger="focus"
              data-bs-placement="top"
              data-bs-content="individuell vereinbarte Pakete können nur über den Support verändert werden"
              aria-label="Information zur Paketanpassung"
            ><Icon icon="fa-pro-solid:circle-info" /></button>
          </span>
        </template>

        <button
          v-else-if="active.ends_at"
          type="button"
          class="btn btn-success package-status-card__action"
          :disabled="reactivating"
          @click="$emit('continue-recurring', active)"
        >
          {{ reactivating
            ? 'Reaktivierung läuft...'
            : `endet am: ${formatEndDate(active.ends_at)} - jetzt reaktivieren` }}
        </button>

        <router-link
          v-else-if="active.id"
          :to="{name: 'packages.manage', params: {id: active.id}, query: routeQuery}"
          class="btn btn-primary package-status-card__action"
        >
          Paket verwalten
        </router-link>

        <router-link
          :to="{name: baseTypeIndexRoute}"
          class="btn btn-outline-primary package-status-card__action package-status-card__jump"
        >
          {{ baseTypeEditLabel }}
        </router-link>
      </div>
    </template>

    <button
      v-else
      type="button"
      class="package-status-card__placeholder"
      @click="$emit('scroll-to-row', row.key)"
    >
      <div class="package-status-card__placeholder-top">
        <div class="package-status-card__eyebrow">Kein Paket aktiv</div>
        <h2 class="package-status-card__title">{{ row.label }}</h2>
      </div>
      <div class="package-status-card__placeholder-plus-wrap">
        <span class="package-status-card__plus">
          <Icon icon="fa-solid:plus" />
        </span>
      </div>
      <span class="package-status-card__placeholder-text">Noch kein aktives Paket. Jetzt passendes Paket auswählen.</span>
      <span class="package-status-card__placeholder-action">Pakete anzeigen</span>
    </button>
  </article>
</template>

<script>
import PriceAmount from '@/components/general/PriceAmount.vue'
import {
  getBaseTypeIndexRoute,
  getBaseTypeTheme,
  getBaseTypeThemeClass,
  getBaseTypeThemeStyle,
} from '@/utils/baseTypeTheme'
import {
  formatEndDate,
  getActivePackagePriceLabel,
  getNextBillingPeriodStartLabel,
  getPackageAssignmentId,
  getPackageTotalPriceCents,
  getTileQuantity,
  isIndividualPackage,
} from '@/utils/packagePolicy'

const BASE_TYPE_ENTRY_LABELS = {
  accommodations: 'Unterkunft',
  offers: 'Angebot',
  events: 'Veranstaltung',
  discover: 'Entdecken',
}

export default {
  name: 'PackageStatusCard',
  components: {PriceAmount},
  emits: ['continue-recurring', 'scroll-to-row'],
  props: {
    row: {type: Object, required: true},
    reactivatingIds: {type: Array, default: () => []},
    routeQuery: {type: Object, default: () => ({})},
  },
  computed: {
    active() {
      return this.row.activePackages[0] || null
    },
    reactivating() {
      return this.active ? this.reactivatingIds.includes(getPackageAssignmentId(this.active)) : false
    },
    baseTypeIndexRoute() {
      return getBaseTypeIndexRoute(this.row.key)
    },
    baseTypeLabel() {
      return getBaseTypeTheme(this.row.key).label
    },
    baseTypeEditLabel() {
      return `${BASE_TYPE_ENTRY_LABELS[this.row.key] || this.baseTypeLabel}-Einträge erstellen und bearbeiten`
    },
  },
  methods: {
    getBaseTypeThemeClass,
    getBaseTypeThemeStyle,
    formatEndDate,
    getActivePackagePriceLabel,
    getNextBillingPeriodStartLabel,
    getPackageTotalPriceCents,
    getTileQuantity,
    isIndividualPackage,
  },
}
</script>

<style scoped lang="scss">
.package-status-card {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  gap: 0.95rem;
  padding: 1.25rem;
  border: 1px solid #d8e1ea;
  background: linear-gradient(180deg, #fbfdff, #f4f8fc);
  box-shadow: 0 8px 22px rgba(16, 39, 61, 0.06);
}

.package-status-card--empty {
  padding: 0;
  background: linear-gradient(180deg, #fffef9, #fff8e8);
  border-color: #f1d7a5;
  box-shadow: 0 8px 22px rgba(134, 95, 16, 0.09);
}

.package-status-card--canceled {
  background: linear-gradient(180deg, #fff8f8, #fff1f1);
  border-color: #f0c5c5;
}

.package-status-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.package-status-card__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.35rem;
  padding: 0 0.5rem;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #0f6f37;
  border: 1px solid #83c69a;
  background: #ecf9f0;
}

.package-status-card__badge--canceled {
  color: #9e2e2e;
  border-color: #e6a6a6;
  background: #fdeeee;
}

.package-status-card__eyebrow {
  margin-bottom: 0.5rem;
  color: #5d7183;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.package-status-card__title {
  margin-bottom: 0.5rem;
  font-size: 1.2rem;
  color: #10273d;
}

.package-status-card__spotlight {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border: 1px solid var(--base-type-border, #dbe6ef);
  background: var(--base-type-soft, #f7fbfd);
  box-shadow: 0 10px 24px rgba(16, 39, 61, 0.08);
}

.package-status-card__spotlight-label {
  color: #5d7183;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.package-status-card__spotlight strong {
  display: block;
  margin-top: 0.3rem;
  color: var(--base-type-text, #10273d);
  font-size: 1.35rem;
  line-height: 1.1;
}

.package-status-card__price-block {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 9rem;
  padding-left: 1.05rem;
  border-left: 1px solid var(--base-type-border, #dbe6ef);
  text-align: right;
}

.package-status-card__price-block > span {
  color: var(--base-type-primary, #0f3553);
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
}

.package-status-card__price-block :deep(.price-amount__value) {
  color: var(--base-type-primary, #0f3553);
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
}

.package-status-card__price-block :deep(.price-amount__suffix) {
  font-size: 0.78rem;
  font-weight: 600;
  color: #5d7183;
}

.package-status-card__price-block :deep(.price-amount__vat) {
  margin-top: 0.15rem;
  text-align: right;
}

.package-status-card__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem 1rem;
}

.package-status-card__facts dt {
  margin-bottom: 0.2rem;
  color: #5d7183;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.package-status-card__facts dd {
  margin: 0;
  color: #10273d;
  font-weight: 600;
}

.package-status-card__fact--full {
  grid-column: 1 / -1;
}

.package-status-card__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 3rem;
  font-weight: 700;
}

.package-status-card__jump {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  color: #000000;
  border-color: #000000;
  background: #ffffff;
  font-weight: 600;
  white-space: normal;
  line-height: 1.25;
}

.package-status-card__jump:hover,
.package-status-card__jump:focus-visible,
.package-status-card__jump:active {
  color: #000000;
  border-color: #000000;
  background: #f7f7f7;
}

.package-status-card__actions {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.package-status-card__placeholder {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  width: 100%;
  height: 100%;
  padding: 1.25rem;
  border: 0;
  background: transparent;
  color: #5d4500;
  text-align: left;
  cursor: pointer;
  animation: packageGlow 1.8s ease-in-out infinite;
}

.package-status-card__placeholder-top {
  display: flex;
  flex-direction: column;
}

.package-status-card__placeholder-plus-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 4.8rem;
}

.package-status-card__plus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border: 1px dashed #d6aa57;
  background: rgba(255, 255, 255, 0.72);
  color: #c78100;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1;
}

.package-status-card__placeholder-text {
  color: #6f5a21;
}

.package-status-card__placeholder-action {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.35rem;
  padding: 0.35rem 0.65rem;
  border: 1px solid #d6aa57;
  color: #7f5600;
  background: rgba(255, 255, 255, 0.6);
  font-weight: 600;
}

.package-tile__cta-wrap {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  width: 100%;
}

.package-tile__cta-wrap .package-status-card__action {
  flex: 1;
}

.package-tile__info-btn {
  width: 2.4rem;
  min-width: 2.4rem;
  min-height: 2.4rem;
  padding: 0;
  border: 1px solid #d2dce6;
  border-radius: 0.2rem;
  background: transparent;
  color: #476582;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

@container (max-width: 27rem) {
  .package-status-card {
    gap: 0.85rem;
    padding: 1rem;
  }

  .package-status-card__header {
    align-items: flex-start;
  }

  .package-status-card__title {
    font-size: 1.35rem;
    line-height: 1.15;
  }

  .package-status-card__spotlight {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 0.8rem;
    padding: 0.95rem;
  }

  .package-status-card__price-block {
    min-width: 0;
    padding-top: 0.8rem;
    padding-left: 0;
    border-top: 1px solid var(--base-type-border, #dbe6ef);
    border-left: 0;
    text-align: left;
  }

  .package-status-card__price-block > span,
  .package-status-card__price-block :deep(.price-amount__value) {
    font-size: 1.35rem;
    letter-spacing: 0;
  }

  .package-status-card__price-block :deep(.price-amount__vat) {
    text-align: left;
  }

  .package-tile__cta-wrap {
    align-items: stretch;
  }

  .package-tile__cta-wrap .package-status-card__action {
    min-width: 0;
  }

  .package-tile__info-btn {
    align-self: stretch;
  }

  .package-status-card__jump {
    min-height: 3.15rem;
    padding-right: 0.6rem;
    padding-left: 0.6rem;
    font-size: 0.9rem;
  }

}

@keyframes packageGlow {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(241, 178, 74, 0.12);
  }

  50% {
    box-shadow: 0 0 0 8px rgba(241, 178, 74, 0.22);
  }
}
</style>
